import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { getConsent, setConsent } from '@/lib/consent';

export const COOKIE_SETTINGS_EVENT = 'mtb-open-cookie-settings';

/** Ouvre la bannière de préférences cookies depuis n'importe où (footer, page RGPD). */
export const openCookieSettings = () =>
  window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));

const CookieConsent = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getConsent() === null) setVisible(true);
    const open = () => setVisible(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  if (!visible || typeof document === 'undefined') return null;

  const fr = language === 'fr';

  const decide = (value: 'granted' | 'denied') => {
    setConsent(value);
    setVisible(false);
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-label={fr ? 'Préférences cookies' : 'Cookie preferences'}
      className="fixed inset-x-0 bottom-0 z-[80] px-3 pb-[calc(env(safe-area-inset-bottom)+68px)] md:pb-3"
    >
      <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-card/95 px-3 py-2.5 shadow-lg backdrop-blur-md">
        <Cookie className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
        <p className="min-w-[12rem] flex-1 text-xs leading-snug text-muted-foreground">
          {fr
            ? 'Cookies de mesure d’audience uniquement avec votre accord.'
            : 'Analytics cookies only with your consent.'}{' '}
          <Link to="/privacy-policy" className="font-medium text-foreground underline underline-offset-2">
            {fr ? 'En savoir plus' : 'Learn more'}
          </Link>
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" className="h-8 px-4 text-xs" onClick={() => decide('granted')}>
            {fr ? 'Accepter' : 'Accept'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-2 px-4 text-xs"
            onClick={() => decide('denied')}
          >
            {fr ? 'Refuser' : 'Decline'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CookieConsent;
